import React, { useState, useEffect } from 'react';
import './DashboardAI.css';
import MuiTable from '../../DashboardComponents/MuiTable/MuiTable';

export default function DashboardAI() {
    const [tableData, setTableData] = useState([]);
    const [clusteredProjects, setClusteredProjects] = useState([]);  
    const [plotImage, setPlotImage] = useState('');  
    const [topics, setTopics] = useState([]);  
    const [clusteringDone, setClusteringDone] = useState(false);  
    const [recommendations, setRecommendations] = useState([]); // State for recommendations


    // State for topic modeling inputs
    const [numComponents, setNumComponents] = useState(5);
    const [method, setMethod] = useState(0);

    // State for clustering inputs
    const [nClusters, setNClusters] = useState(3);
    const [embeddingMethod, setEmbeddingMethod] = useState("tf-idf");
    const [cluster_by, setcluster_by] = useState("project_description")

      // State for recommendation inputs
    const [query, setQuery] = useState(''); // query input
    const [top_k, setTopK] = useState(5); // top_k input

    const [viewMode, setViewMode] = useState('table'); 

    // Fetch Topic Modeling
    const fetchTopicModelling = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_DASHBOARD_URL}/model_topics?num_components=${numComponents}&method=${method}`);
            const data = await response.json();
            if (response.ok) {
                setTopics(data.result);
            } else {
                console.error('Error fetching topic modelling:', data.error);
            }
        } catch (error) {
            console.error('Error:', error);
        }
    };

    // Fetch Clustering
    const fetchClusteredProjects = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_DASHBOARD_URL}/cluster_projects?cluster_by=${cluster_by}&embedding_method=${embeddingMethod}&n_clusters=${nClusters}`);
            const data = await response.json();
            if (response.ok) {
                setClusteredProjects(data.clustered_projects);
                setTableData(data.clustered_projects);
                setPlotImage(data.plot_image);
                setClusteringDone(true);  // Show toggle buttons after clustering
            } else {
                console.error('Error fetching clustered projects:', data.error);
            }
        } catch (error) {
            console.error('Error:', error);
        }
    };

    // Fetch Recommendations
    const fetchRecommendations = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_DASHBOARD_URL}/get_recommendations?query=${query}&top_k=${top_k}&method=flat`);
            const data = await response.json();
            if (response.ok) {
                setRecommendations(data);  // Set the recommendations in the state
            } else {
                console.error('Error fetching recommendations:', data.error);
            }
        } catch (error) {
            console.error('Error:', error);
        }
    };

    return (
        <div className="dashboard-container">

            {/* Topic Modeling Section */}
            <div className="input-section">
                <h3>Topic Modeling</h3>
                <div className="input-card">
                    <label>Number of Components:</label>
                    <input 
                        type="number" 
                        value={numComponents} 
                        onChange={(e) => setNumComponents(e.target.value)} 
                    />
                    <label>Method:</label>
                    <select value={method} onChange={(e) => setMethod(e.target.value)}>
                        <option value="0">LDA</option>
                        <option value="1">LSA</option>
                    </select>

                    <button className="green-button" onClick={fetchTopicModelling}>
                        Run Topic Modeling
                    </button>
                </div>
                <div className='output-container'>
                    {topics.length > 0 && (
                        <div className="output-card">
                            <h3>Model Topics</h3>
                            <ul>
                                {topics.map((topic, index) => (
                                    <li key={index}>
                                        <strong>Topic {index + 1}:</strong> {topic.join(' ')}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            </div>

            {/* Recommendations Section */}
            <div className="input-section">
                <h3>Project Recommendations</h3>
                <div className="input-card">
                    <label>Search Query:</label>
                    <input 
                        type="text" 
                        value={query} 
                        onChange={(e) => setQuery(e.target.value)} 
                    />

                    <label>Top K Recommendations:</label>
                    <input 
                        type="number" 
                        value={top_k} 
                        onChange={(e) => setTopK(e.target.value)} 
                    />

                    <button className="green-button" onClick={fetchRecommendations}>
                        Get Recommendations
                    </button>
                </div>

                <div className='output-container'>
                    {recommendations.length > 0 && (
                        <div className="output-card">
                            <h3>Recommended Projects</h3>
                            <ul>
                                {recommendations.map((project, index) => (
                                    <li key={index}>
                                        <strong>{project.title}</strong>: {project.details}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            </div>

            {/* Clustering Section */}
            <div className="input-section">
                <h3>Clustering</h3>
                <div className="input-card">
                    <label>Number of Clusters:</label>
                    <input 
                        type="number" 
                        value={nClusters} 
                        onChange={(e) => setNClusters(e.target.value)} 
                    />
                    <label>Embedding Method:</label>
                    <select value={embeddingMethod} onChange={(e) => setEmbeddingMethod(e.target.value)}>
                        <option value="tf-idf">TF-IDF</option>
                        <option value="GLoVE">GLoVE</option>
                    </select>

                    <label>Cluster By:</label>
                    <select value={cluster_by} onChange={(e) => setcluster_by(e.target.value)}>
                        <option value="project_name">Name</option>
                        <option value="project_tag">Tag</option>
                        <option value="project_description">Description</option>
                    </select>
                    <button className="green-button" onClick={fetchClusteredProjects}>
                        Run Clustering
                    </button>
                </div>

                <div className='output-container'>
                    {clusteringDone && (
                        <div className="toggle-buttons">
                            <button 
                                className={viewMode === 'table' ? 'active' : ''} 
                                onClick={() => setViewMode('table')}
                            >
                                View Table
                            </button>
                            <button 
                                className={viewMode === 'plot' ? 'active' : ''} 
                                onClick={() => setViewMode('plot')}
                            >
                                View Plot
                            </button>
                        </div>
                    )}
                    {clusteringDone && (
                        <div className="view-container">
                            {viewMode === 'table' ? (
                                <MuiTable tableData={tableData} />
                            ) : (
                                plotImage && (
                                    <div className="plot-container">
                                        <h3>K-Means Cluster Plot</h3>
                                        <img src={`data:image/png;base64,${plotImage}`} alt="Cluster Plot" />
                                    </div>
                                )
                            )}
                        </div>
                    )}
                </div>
            </div>

            

        </div>
    );
}
